/**
 * Tab de configurações gerais de um Event Type
 * 
 * Permite editar: título, descrição, duração, cor, buffers,
 * limites de antecedência, provider de conferência, integração CRM, etc.
 * 
 * RELEASE: 2026-02-20
 * VERSION: 1.1.0 - Adicionada seção de Integração CRM
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Save, User, Phone, Mail, Variable, VideoOff, Calendar } from 'lucide-react';
import googleMeetIcon from '@/assets/google-meet-icon.png';
import googleCalendarIcon from '@/assets/google-calendar-icon.png';
import {
  type SchedulingEventType,
  useSchedulingEventTypes,
  useCalendarIntegrations,
} from '@/hooks/useSchedulingEventTypes';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { MediaUrlInput } from '@/components/ui/media-url-input';
import { CrmIntegrationSection, type CrmIntegrationFormData } from './CrmIntegrationSection';
import { showToast } from '@/lib/toast';

interface EventTypeGeneralTabProps {
  eventType: SchedulingEventType;
}

/** Opções de cores */
const COLOR_OPTIONS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316',
];

/** Variáveis permitidas no campo título do evento */
const TITLE_VARIABLES = [
  { key: 'nome_completo', label: 'nome_completo', icon: User },
  { key: 'email', label: 'email', icon: Mail },
  { key: 'telefone', label: 'telefone', icon: Phone },
];

export function EventTypeGeneralTab({ eventType }: EventTypeGeneralTabProps) {
  const { updateEventType, isUpdating } = useSchedulingEventTypes();
  const { activeIntegrations, isLoading: isLoadingCalendars } = useCalendarIntegrations();

  /** Ref do input de título para inserir variáveis na posição do cursor */
  const titleInputRef = useRef<HTMLInputElement>(null);

  /**
   * Insere variável na posição do cursor no input de título.
   * Se não há cursor posicionado, insere no final.
   */
  const handleInsertVariable = (variableKey: string) => {
    const input = titleInputRef.current;
    const variable = `{{${variableKey}}}`;
    
    if (input) {
      const start = input.selectionStart ?? form.title.length;
      const end = input.selectionEnd ?? form.title.length;
      const newTitle = form.title.slice(0, start) + variable + form.title.slice(end);
      setForm((p) => ({ ...p, title: newTitle }));
      
      // Reposicionar cursor após a variável inserida
      requestAnimationFrame(() => {
        const newPos = start + variable.length;
        input.setSelectionRange(newPos, newPos);
        input.focus();
      });
    } else {
      setForm((p) => ({ ...p, title: p.title + variable }));
    }
  };

  // Estado local do formulário (inicializado com os dados do event type)
  const [form, setForm] = useState({
    title: eventType.title,
    description: eventType.description || '',
    duration_minutes: eventType.duration_minutes,
    color: eventType.color || '#3B82F6',
    min_notice_hours: eventType.min_notice_hours,
    max_advance_days: eventType.max_advance_days,
    max_bookings_per_day: eventType.max_bookings_per_day,
    buffer_before_minutes: eventType.buffer_before_minutes,
    buffer_after_minutes: eventType.buffer_after_minutes,
    conferencing_provider: eventType.conferencing_provider,
    requires_approval: eventType.requires_approval,
    confirmation_message: eventType.confirmation_message || '',
    logo_url: eventType.logo_url || '',
    /** Agendamentos concorrentes por slot (v1.5.0) */
    max_concurrent_bookings_per_slot: eventType.max_concurrent_bookings_per_slot ?? 1,
    /** Calendário vinculado (v2.0.0) */
    calendar_integration_id: eventType.calendar_integration_id || '',
  });

  // Estado do formulário CRM (separado para clareza)
  const [crmForm, setCrmForm] = useState<CrmIntegrationFormData>({
    crm_auto_create_deal: eventType.crm_auto_create_deal ?? false,
    crm_workspace_id: eventType.crm_workspace_id ?? null,
    crm_pipeline_id: eventType.crm_pipeline_id ?? null,
    crm_phase_id: eventType.crm_phase_id ?? null,
    crm_source: eventType.crm_source ?? null,
    crm_seller_assignment_mode: eventType.crm_seller_assignment_mode ?? 'none',
    crm_seller_id: eventType.crm_seller_id ?? null,
    crm_distribution_queue_id: eventType.crm_distribution_queue_id ?? null,
  });

  /** Sincroniza form quando eventType muda (ex: após salvar) */
  useEffect(() => {
    setForm({
      title: eventType.title,
      description: eventType.description || '',
      duration_minutes: eventType.duration_minutes,
      color: eventType.color || '#3B82F6',
      min_notice_hours: eventType.min_notice_hours,
      max_advance_days: eventType.max_advance_days,
      max_bookings_per_day: eventType.max_bookings_per_day,
      buffer_before_minutes: eventType.buffer_before_minutes,
      buffer_after_minutes: eventType.buffer_after_minutes,
      conferencing_provider: eventType.conferencing_provider,
      requires_approval: eventType.requires_approval,
      confirmation_message: eventType.confirmation_message || '',
      logo_url: eventType.logo_url || '',
      max_concurrent_bookings_per_slot: eventType.max_concurrent_bookings_per_slot ?? 1,
      calendar_integration_id: eventType.calendar_integration_id || '',
    });
    setCrmForm({
      crm_auto_create_deal: eventType.crm_auto_create_deal ?? false,
      crm_workspace_id: eventType.crm_workspace_id ?? null,
      crm_pipeline_id: eventType.crm_pipeline_id ?? null,
      crm_phase_id: eventType.crm_phase_id ?? null,
      crm_source: eventType.crm_source ?? null,
      crm_seller_assignment_mode: eventType.crm_seller_assignment_mode ?? 'none',
      crm_seller_id: eventType.crm_seller_id ?? null,
      crm_distribution_queue_id: eventType.crm_distribution_queue_id ?? null,
    });
  }, [eventType]);

  /** Salva alterações (incluindo campos CRM) */
  const handleSave = async () => {
    // Validação obrigatória dos campos CRM quando toggle ativo
    if (crmForm.crm_auto_create_deal) {
      const missing: string[] = [];
      if (!crmForm.crm_workspace_id) missing.push('Workspace');
      if (!crmForm.crm_pipeline_id) missing.push('Pipeline');
      if (!crmForm.crm_phase_id) missing.push('Fase');
      if (!crmForm.crm_source) missing.push('Origem');
      if (crmForm.crm_seller_assignment_mode === 'manual' && !crmForm.crm_seller_id)
        missing.push('Vendedor');
      if (crmForm.crm_seller_assignment_mode === 'distribution_queue' && !crmForm.crm_distribution_queue_id)
        missing.push('Fila de distribuição');

      if (missing.length > 0) {
        showToast.error(
          'Campos obrigatórios do CRM',
          `Preencha: ${missing.join(', ')}`
        );
        return;
      }
    }

    await updateEventType({
      id: eventType.id,
      title: form.title,
      description: form.description || null,
      duration_minutes: form.duration_minutes,
      color: form.color,
      min_notice_hours: form.min_notice_hours,
      max_advance_days: form.max_advance_days,
      max_bookings_per_day: form.max_bookings_per_day,
      buffer_before_minutes: form.buffer_before_minutes,
      buffer_after_minutes: form.buffer_after_minutes,
      conferencing_provider: form.conferencing_provider,
      requires_approval: form.requires_approval,
      confirmation_message: form.confirmation_message || null,
      logo_url: form.logo_url || null,
      // Agendamentos concorrentes por slot (v1.5.0)
      max_concurrent_bookings_per_slot: form.max_concurrent_bookings_per_slot,
      // Calendário vinculado (v2.0.0)
      calendar_integration_id: form.calendar_integration_id || null,
      // Campos CRM (v1.3.0)
      crm_auto_create_deal: crmForm.crm_auto_create_deal,
      crm_workspace_id: crmForm.crm_workspace_id,
      crm_pipeline_id: crmForm.crm_pipeline_id,
      crm_phase_id: crmForm.crm_phase_id,
      crm_source: crmForm.crm_source,
      crm_seller_assignment_mode: crmForm.crm_seller_assignment_mode,
      crm_seller_id: crmForm.crm_seller_id,
      crm_distribution_queue_id: crmForm.crm_distribution_queue_id,
    });

    // Trigger trg_sync_slot_interval no banco sincroniza slot_interval_minutes
    // automaticamente quando duration_minutes é alterado (SECURITY DEFINER, bypassa RLS)
  };

  return (
    <div className="space-y-6">
      {/* Informações Básicas */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Informações Básicas</CardTitle>
          <CardDescription>Configure o título, descrição e duração do evento</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Título</Label>
              <Input
                ref={titleInputRef}
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                maxLength={200}
                placeholder="Ex: Apresentação {{nome_completo}}"
              />
              {/* Badges de variáveis dinâmicas para inserir no título */}
              <div className="flex flex-wrap gap-1.5">
                {TITLE_VARIABLES.map(({ key, label, icon: Icon }) => (
                  <Button
                    key={key}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 gap-1 text-xs hover:bg-primary/10 hover:border-primary"
                    onClick={() => handleInsertVariable(key)}
                  >
                    <Icon className="h-3 w-3" />
                    {`{{${label}}}`}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Duração (minutos)</Label>
              <Select
                value={String(form.duration_minutes)}
                onValueChange={(v) => setForm((p) => ({ ...p, duration_minutes: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[15, 30, 45, 60, 90, 120].map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d >= 60 ? `${d / 60}h${d % 60 ? ` ${d % 60}min` : ''}` : `${d} min`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              maxLength={500}
              rows={3}
            />
          </div>
          {/* Logo do evento + Calendário Vinculado (mesma linha, 1/2 colunas) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Logo do Evento</Label>
              <MediaUrlInput
                value={form.logo_url || null}
                onChange={(url) => setForm((p) => ({ ...p, logo_url: url || '' }))}
                placeholder="Selecione uma imagem da galeria"
                showPreview
                allowedTypes={['image']}
              />
            </div>
            <div className="space-y-2">
              <Label>Calendário Vinculado</Label>
              <Select
                value={form.calendar_integration_id}
                onValueChange={(v) => setForm((p) => ({ ...p, calendar_integration_id: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Nenhum calendário vinculado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {activeIntegrations.map((cal) => (
                    <SelectItem key={cal.id} value={cal.id}>
                      <span className="flex items-center gap-2">
                        <img src={googleCalendarIcon} alt="Google Calendar" className="h-3.5 w-3.5" />
                        {cal.provider === 'google' ? 'Google Calendar' : cal.provider}
                        {cal.provider_email && <span className="text-muted-foreground ml-1">({cal.provider_email})</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Gerencie calendários no botão "Calendários" da página principal de agendamento.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Cor</Label>
            <div className="flex gap-2 flex-wrap">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`h-8 w-8 rounded-full border-2 transition-all ${
                    form.color === c
                      ? 'border-foreground scale-110'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setForm((p) => ({ ...p, color: c }))}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configurações Avançadas */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Configurações Avançadas</CardTitle>
          <CardDescription>Folgas entre reuniões, limites e provedor de conferência</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Antecedência mínima (horas)</Label>
              <Input
                type="number"
                min={0}
                max={168}
                value={form.min_notice_hours}
                onChange={(e) => setForm((p) => ({ ...p, min_notice_hours: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Máx. dias à frente</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={form.max_advance_days}
                onChange={(e) => setForm((p) => ({ ...p, max_advance_days: parseInt(e.target.value) || 30 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Folga antes (min)</Label>
              <p className="text-xs text-muted-foreground">Tempo livre antes da reunião para preparação</p>
              <Input
                type="number"
                min={0}
                max={120}
                value={form.buffer_before_minutes}
                onChange={(e) => setForm((p) => ({ ...p, buffer_before_minutes: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Folga depois (min)</Label>
              <p className="text-xs text-muted-foreground">Tempo livre após a reunião para anotações ou descanso</p>
              <Input
                type="number"
                min={0}
                max={120}
                value={form.buffer_after_minutes}
                onChange={(e) => setForm((p) => ({ ...p, buffer_after_minutes: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Máx. agendamentos/dia</Label>
              <Input
                type="number"
                min={0}
                value={form.max_bookings_per_day ?? ''}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    max_bookings_per_day: e.target.value ? parseInt(e.target.value) : null,
                  }))
                }
                placeholder="Ilimitado"
              />
            </div>
            <div className="space-y-2">
              <Label>Conferência</Label>
              <Select
                value={form.conferencing_provider}
                onValueChange={(v) => setForm((p) => ({ ...p, conferencing_provider: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google_meet">
                    <span className="flex items-center gap-2">
                      <img src={googleMeetIcon} alt="Google Meet" className="h-4 w-4" />
                      Google Meet
                    </span>
                  </SelectItem>
                  <SelectItem value="none">
                    <span className="flex items-center gap-2">
                      <VideoOff className="h-4 w-4 text-muted-foreground" />
                      Sem conferência
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Toggle: Requer aprovação */}
          <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
            <div>
              <p className="font-medium text-sm text-foreground">Requer aprovação</p>
              <p className="text-xs text-muted-foreground">
                Agendamentos precisam ser aprovados manualmente
              </p>
            </div>
            <Switch
              checked={form.requires_approval}
              onCheckedChange={(v) => setForm((p) => ({ ...p, requires_approval: v }))}
            />
          </div>

          {/* Mensagem de confirmação */}
          <div className="space-y-2">
            <Label>Mensagem de confirmação</Label>
            <Textarea
              value={form.confirmation_message}
              onChange={(e) => setForm((p) => ({ ...p, confirmation_message: e.target.value }))}
              placeholder="Mensagem exibida ao confirmar o agendamento..."
              maxLength={1000}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Integração CRM (inclui agendamentos simultâneos como último campo) */}
      <CrmIntegrationSection
        form={crmForm}
        onChange={(updates) => setCrmForm((prev) => ({ ...prev, ...updates }))}
        maxConcurrentBookings={form.max_concurrent_bookings_per_slot}
        onMaxConcurrentChange={(value) => setForm((p) => ({ ...p, max_concurrent_bookings_per_slot: value }))}
      />

      {/* Botão Salvar */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isUpdating} className="gap-2">
          <Save className="h-4 w-4" />
          {isUpdating ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </div>
    </div>
  );
}
